import { useEffect, useState } from "react"
import { sdkContextStatus } from "src/context/SDKContext"
import { useQuery, useQueryClient } from "react-query"
import { useRecoilValue } from "recoil"
import { localUserIdAtom } from "src/recoil/atoms/users"
import { brandingSelector } from "src/recoil/atoms/room"
import { useLocation } from "react-router-dom"
import { usePrevious } from "@chakra-ui/react"
import { z } from "zod"
import { queryClient } from "src/shared/queryClient"
import { ConsoleLogger } from "src/util/logger"

const logger = new ConsoleLogger()

const loading = z.literal("loading")
const emptyString = z.literal("")
const platformNames = z
  .literal("Utta")
  .or(z.literal("LearnMachine"))
  .or(z.literal("LiveSell"))
  .or(z.literal("x121"))

const Config = z
  .object({
    roomAvatars: z.boolean().or(loading).or(loading),
    endUserRegistration: z.boolean().or(loading),
    endUserLogin: z.boolean().or(loading),
    personalRooms: z.boolean().or(loading),
    avatarsGrid: z.boolean().or(loading),
    journey: z.boolean().or(loading),
    chatAttachment: z.boolean().or(loading),
    chatAttachmentVideo: z.boolean().or(loading),
    whiteboard: z.boolean().or(loading),
    shoppableVideos: z.string().or(loading),
    entitlement: z.string().or(loading),
    mainCustomer: z.boolean().or(loading),
    postLoginRedirectUrl: z
      .string()
      .startsWith("/")
      .or(emptyString)
      .or(loading),
    learnmachineAuthPage: z.boolean().or(loading),
    endUserRequireEmailVerification: z.boolean().or(loading),
    stripePk: z.string().or(loading),
    oauthRedirectUri: z
      .string()
      .startsWith("https://")
      .or(emptyString)
      .or(loading),
    googleOauthId: z.string().endsWith(".com").or(emptyString).or(loading),
    appleOauthClientId: z
      .string()
      .startsWith("com.")
      .or(emptyString)
      .or(loading),
    localMuteEnabled: z.boolean().or(loading),
    localCamEnabled: z.boolean().or(loading),
    localSettingsEnabled: z.boolean().or(loading),
    localShareEnabled: z.boolean().or(loading),
    localStreamEnabled: z.boolean().or(loading),
    localRecordEnabled: z.boolean().or(loading),
    defaultFullScreen: z.boolean().or(loading),
    sidepanelUsersEnabled: z.boolean().or(loading),
    sidepanelChatEnabled: z.boolean().or(loading),
    sidepanelLinksEnabled: z.boolean().or(loading),
    sidepanelFeaturedEnabled: z.boolean().or(loading),
    sidepanelVideosEnabled: z.boolean().or(loading),
    sidepanelTaggedEnabled: z.boolean().or(loading),
    sidepanelPollsEnabled: z.boolean().or(loading),
    sidepanelControlsEnabled: z.boolean().or(loading),
    sidepanelSettingsEnabled: z.boolean().or(loading),
    hidePersonalRoomTitle: z.boolean().or(loading),
    requestPermissionsOnJoinPageLoad: z.boolean().or(loading),
    videoPlayerShareEnabled: z.boolean().or(loading),
    videoPlayerHandEnabled: z.boolean().or(loading),
    videoPlayerHeartEnabled: z.boolean().or(loading),
    uttaAuthPage: z.boolean().or(loading),
    platformName: platformNames.or(loading),
    chatInputBoxOverlayOverride: z.boolean().nullable().or(loading),
    mobileChatShowUserMessagesRight: z.boolean().or(loading),
    shoppableVideosMobileTabVisibleOverride: z.boolean().nullable().or(loading),
    avatarChatFilter: z.boolean().or(loading),
    swapLeaveAndMicSpeakerButtonPositions: z.boolean().or(loading),
    iosAppUrlScheme: z.string().nullable().or(loading),
    sidepanelInspirationsEnabled: z.boolean().or(loading),
    voiceAndVideoEnabled: z.boolean().or(loading),
    watchPartyEnabled: z.boolean().or(loading),
    showAttachmentInMvwOnSend: z.boolean().or(loading),
    sentryEnabled: z.boolean().or(loading),
    stripeProductIds: z.string().nullable().or(loading),
    hideInspirationCategories: z.optional(z.boolean()).or(loading),
    disableReplies: z.boolean().or(loading),
    enableOnboarding: z.boolean().or(loading),
    sidepanelShoppableVideosEnabled: z.boolean().or(loading)
  })
  .strict()

export const QUERY_KEY = ["requestx-config"]

const handler = {
  get(target, prop, receiver) {
    if (typeof target[prop] === "undefined") {
      return "loading"
    }
    return target[prop]
  },
  set() {
    logger.error("Do not modify config")
    return null
  }
}

function snakeToCamel(snakeCaseString: string) {
  let camelCaseString = ""
  for (let i = 0; i < snakeCaseString.length; i++) {
    if (snakeCaseString[i] === "_") {
      camelCaseString += snakeCaseString[i + 1].toUpperCase()
      i++
      continue
    }
    camelCaseString += snakeCaseString[i]
  }
  return camelCaseString
}

type ConfigT = z.infer<typeof Config>

function mapConfigs(data: Record<string, string>) {
  function transform(value: string) {
    if (value === "true") return true
    if (value === "false") return false
    return value
  }

  const configData = {}
  for (const value of Object.values(data)) {
    configData[snakeToCamel(value["configName"])] = transform(
      value["configValue"]
    )
  }

  try {
    Config.parse(configData)
  } catch (error) {
    // logger.error("Error validating config: ", error.message)
  }

  return configData
}

export type Configs = ReturnType<typeof useConfig>

const useConfig = function () {
  const userId = useRecoilValue(localUserIdAtom)
  const branding = useRecoilValue(brandingSelector)
  const { customerId } = branding ?? {}
  const { pathname: roomId } = useLocation()

  const prevUserId = usePrevious(userId)
  const prevCustomerId = usePrevious(customerId)
  const prevRoomId = usePrevious(roomId)

  const [config, setConfig] = useState<ConfigT>(new Proxy({}, handler))

  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: QUERY_KEY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const meetingClient = await sdkContextStatus
      const response = await meetingClient.validateAndGetRoomBranding()
      return response
    }
  })

  useEffect(() => {
    if (!data || !data.clientConfig) return
    const configData = mapConfigs(data.clientConfig.data)
    setConfig(new Proxy(configData, handler))
  }, [data])

  useEffect(() => {
    if (!prevUserId || !prevCustomerId || !prevRoomId) {
      return
    }

    const invalidate =
      userId !== prevUserId ||
      customerId !== prevCustomerId ||
      roomId !== prevRoomId

    if (invalidate) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    }
  }, [userId, prevUserId, customerId, prevCustomerId, roomId, prevRoomId])

  return config
}

export const getConfigs: () => Promise<ConfigT> = async () => {
  const data = queryClient.fetchQuery({
    queryKey: QUERY_KEY,
    staleTime: 1000,
    queryFn: async () => {
      const meetingClient = await sdkContextStatus
      const response = await meetingClient.validateAndGetRoomBranding()
      return response
    }
  })

  return data.then(({ clientConfig }) => {
    return new Proxy(mapConfigs(clientConfig.data), handler)
  })
}

export default useConfig
